import React, { forwardRef } from "react"
import { AppText, AppTextProps } from "./app-text"

interface FormattedAppTextProps extends Omit<AppTextProps, "children"> {
  text: string
}

interface FormatRule {
  regex: RegExp
  className: string
  transform?: (match: string, groups: string[]) => string
}

const FORMAT_RULES: Record<string, FormatRule> = {
  url: {
    regex: /(https?:\/\/[^\s]+)/g,
    className: "underline break-all",
  },
  mention: {
    regex: /{@}\[([^\]]+)\]\(([^)]+)\)/g,
    className: "font-bold",
    transform: (_, groups) => `@${groups[0]}`,
  },
}

const formatTextWithRule = (
  nodes: React.ReactNode[],
  rule: FormatRule,
  parentClassName: string,
  variant?: AppTextProps["variant"]
): React.ReactNode[] => {
  return nodes.flatMap((node) => {
    if (typeof node !== "string") {
      return node
    }
    const result: React.ReactNode[] = []
    let lastIndex = 0
    rule.regex.lastIndex = 0
    let match
    while ((match = rule.regex.exec(node)) !== null) {
      if (match.index > lastIndex) {
        result.push(node.slice(lastIndex, match.index))
      }
      const transformedText = rule.transform ? rule.transform(match[0], match.slice(1)) : match[0]
      result.push(
        <AppText
          as="span"
          variant={variant}
          key={`${rule.className}-${match.index}`}
          className={[parentClassName, rule.className].filter(Boolean).join(" ")}
        >
          {transformedText}
        </AppText>
      )
      lastIndex = rule.regex.lastIndex
    }
    if (lastIndex < node.length) {
      result.push(node.slice(lastIndex))
    }
    return result
  })
}

export const FormattedAppText = forwardRef<HTMLElement, FormattedAppTextProps>(
  ({ text, ...props }, ref) => {
    const { className = "", variant } = props
    const formatText = (inputText: string): React.ReactNode[] => {
      let formattedNodes: React.ReactNode[] = [inputText]
      Object.values(FORMAT_RULES).forEach((rule) => {
        formattedNodes = formatTextWithRule(formattedNodes, rule, className, variant)
      })
      return formattedNodes
    }
    return (
      <AppText ref={ref} {...props}>
        {formatText(text)}
      </AppText>
    )
  }
)
